/**
 * v2 Settings → Schedules. List + create + edit + delete.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api } from "../../../api/client";
import { LocalTime } from "../../../lib/datetime";
import { Badge } from "../../components/Badge";
import { CopyableCell } from "../../components/CopyableCell";
import { useToast } from "../../components/Toast";
import { HelpButton } from "../../help/HelpPanel";

type Schedule = {
  id: number;
  name: string;
  job_name: string;
  cron_expr: string | null;
  interval_seconds: number | null;
  policy: string;
  enabled: boolean;
  chassis_id: number | null;
  chassis_name: string | null;
  last_run_at: string | null;
  next_run_at: string | null;
};
type Resp = { schedules: Schedule[] };

export function Schedules() {
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const [creating, setCreating] = useState(false);

  const q = useQuery<Resp>({
    queryKey: ["v2", "schedules"],
    queryFn: async () => {
      const res = await api.get<Resp>("/schedules");
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const create = useMutation({
    mutationFn: async (body: ScheduleCreateBody) => {
      const res = await api.post<{ id: number }>("/schedules", body);
      return res.data;
    },
    onSuccess: (data) => {
      toast.push("Schedule created", "success");
      qc.invalidateQueries({ queryKey: ["v2", "schedules"] });
      setCreating(false);
      // Bounce to the detail page so the operator can verify
      // params + run history without an extra click.
      nav(`/settings/schedules/${data.id}`);
    },
    onError: () => toast.push("Create failed — check the form", "error"),
  });

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-100">Schedules</h1>
            <HelpButton topic="settings-schedules" />
          </div>
          <p className="text-xs text-slate-500">
            Recurring jobs (cron expressions or interval-based) per
            chassis or global. The scheduler service ticks every
            second; ``next_run_at`` is when the row is up next.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-900/60"
        >
          <Plus className="h-3 w-3" />
          New schedule
        </button>
      </header>
      {creating && (
        <ScheduleForm
          onSave={(body) => create.mutate(body)}
          onCancel={() => setCreating(false)}
          saving={create.isPending}
        />
      )}
      <div className="rounded-lg bg-slate-900 shadow-border p-4">
        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : (q.data?.schedules.length ?? 0) === 0 ? (
          <p className="text-xs text-slate-500">No schedules defined.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">Name</th>
                <th className="px-2 py-1">Job</th>
                <th className="px-2 py-1">Trigger</th>
                <th className="px-2 py-1">Chassis</th>
                <th className="px-2 py-1">State</th>
                <th className="px-2 py-1">Last run</th>
                <th className="px-2 py-1">Next run</th>
                <th className="px-2 py-1 text-right">Open</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {(q.data?.schedules ?? []).map((s) => (
                <tr key={s.id}>
                  <td className="px-2 py-1.5">
                    <CopyableCell value={s.name} />
                  </td>
                  <td className="px-2 py-1.5 font-mono">{s.job_name}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-400">
                    {s.cron_expr ?? `every ${s.interval_seconds}s`}
                  </td>
                  <td className="px-2 py-1.5">
                    {s.chassis_id ? (
                      <Link
                        to={`/chassis/${s.chassis_id}`}
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        {s.chassis_name ?? `chassis-${s.chassis_id}`}
                      </Link>
                    ) : (
                      <Badge tone="info">global</Badge>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <Badge tone={s.enabled ? "success" : "muted"}>
                      {s.enabled ? "enabled" : "disabled"}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={s.last_run_at} />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={s.next_run_at} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Link
                      to={`/settings/schedules/${s.id}`}
                      className="text-emerald-400 hover:text-emerald-300"
                    >
                      open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

type ScheduleDetailRow = Schedule & {
  params_json: Record<string, unknown> | null;
  jitter_seconds: number | null;
};

export function ScheduleDetail({ scheduleId }: { scheduleId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const [editing, setEditing] = useState(false);

  type Runs = {
    runs: {
      id: number;
      eval_at: string;
      action: string;
      detail: Record<string, unknown> | null;
    }[];
  };
  const d = useQuery<ScheduleDetailRow>({
    queryKey: ["v2", "schedules", scheduleId],
    queryFn: async () => {
      const res = await api.get<ScheduleDetailRow>(`/schedules/${scheduleId}`);
      return res.data;
    },
  });
  const runs = useQuery<Runs>({
    queryKey: ["v2", "schedules", scheduleId, "runs"],
    queryFn: async () => {
      const res = await api.get<Runs>(`/schedules/${scheduleId}/runs?limit=25`);
      return res.data;
    },
  });

  const update = useMutation({
    mutationFn: async (body: Partial<ScheduleCreateBody>) => {
      // Server uses PATCH semantics — only non-null fields apply.
      const res = await api.patch(`/schedules/${scheduleId}`, body);
      return res.data;
    },
    onSuccess: () => {
      toast.push("Schedule updated", "success");
      qc.invalidateQueries({ queryKey: ["v2", "schedules", scheduleId] });
      qc.invalidateQueries({ queryKey: ["v2", "schedules"] });
      setEditing(false);
    },
    onError: () => toast.push("Update failed", "error"),
  });

  const del = useMutation({
    mutationFn: async () => api.delete(`/schedules/${scheduleId}`),
    onSuccess: () => {
      toast.push("Schedule deleted", "success");
      qc.invalidateQueries({ queryKey: ["v2", "schedules"] });
      nav("/settings/schedules");
    },
    onError: () => toast.push("Delete failed", "error"),
  });

  return (
    <section className="flex flex-col gap-3">
      <Link
        to="/settings/schedules"
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
      >
        <ChevronLeft className="h-3 w-3" />
        Back to schedules
      </Link>
      <header className="flex items-baseline justify-between">
        <h1 className="text-lg font-semibold text-slate-100">
          {d.data?.name ?? "schedule"}
        </h1>
        <HelpButton topic="settings-schedules" />
        {d.data && !editing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </button>
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    `Delete schedule "${d.data!.name}"? Running jobs aren't affected; the schedule simply stops firing.`,
                  )
                ) {
                  del.mutate();
                }
              }}
              disabled={del.isPending}
              className="inline-flex items-center gap-1 rounded border border-red-800 bg-red-900/30 px-2 py-1 text-xs text-red-200 hover:bg-red-900/50 disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </button>
          </div>
        )}
      </header>

      {editing && d.data && (
        <ScheduleForm
          initial={d.data}
          onSave={(body) => update.mutate(body)}
          onCancel={() => setEditing(false)}
          saving={update.isPending}
        />
      )}

      {!editing && (
        <section className="rounded-lg bg-slate-900 shadow-border p-4">
          <h2 className="mb-2 text-sm font-medium text-slate-200">Configuration</h2>
          {d.isLoading ? (
            <p className="text-xs text-slate-500">Loading…</p>
          ) : (
            <dl className="grid grid-cols-2 gap-2 text-xs">
              <dt className="text-slate-500">Job</dt>
              <dd className="font-mono">{d.data?.job_name}</dd>
              <dt className="text-slate-500">Trigger</dt>
              <dd className="font-mono">
                {d.data?.cron_expr ?? `every ${d.data?.interval_seconds}s`}
              </dd>
              <dt className="text-slate-500">Policy</dt>
              <dd>{d.data?.policy}</dd>
              <dt className="text-slate-500">Jitter</dt>
              <dd>{d.data?.jitter_seconds ?? 0}s</dd>
              <dt className="text-slate-500">Enabled</dt>
              <dd>
                <Badge tone={d.data?.enabled ? "success" : "muted"}>
                  {d.data?.enabled ? "enabled" : "disabled"}
                </Badge>
              </dd>
              <dt className="text-slate-500">Params</dt>
              <dd>
                <pre className="overflow-x-auto rounded bg-slate-950 p-1.5 font-mono text-[10px] text-slate-400">
                  {d.data?.params_json
                    ? JSON.stringify(d.data.params_json, null, 2)
                    : "—"}
                </pre>
              </dd>
            </dl>
          )}
        </section>
      )}
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">Recent runs</h2>
        {runs.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : (runs.data?.runs.length ?? 0) === 0 ? (
          <p className="text-xs text-slate-500">No runs recorded yet.</p>
        ) : (
          <ul className="flex flex-col gap-1 font-mono text-[11px]">
            {(runs.data?.runs ?? []).map((r) => (
              <li key={r.id} className="text-slate-300">
                <span className="mr-2 text-slate-500">
                  <LocalTime iso={r.eval_at} />
                </span>
                {r.action}
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

// ----------------------------------------------------------------------
// Schedule create/edit form — shared between the list page (create
// flow) and detail page (edit flow). PATCH semantics on update means
// we send the whole body; the backend ignores no-ops.
// ----------------------------------------------------------------------

type TriggerKind = "cron" | "interval";

export type ScheduleCreateBody = {
  name: string;
  job_name: string;
  cron_expr: string | null;
  interval_seconds: number | null;
  chassis_id: number | null;
  policy: string | null;
  jitter_seconds: number;
  params_json: Record<string, unknown> | null;
  enabled: boolean;
};

const inputCls =
  "w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 focus-visible:border-emerald-700 focus-visible:outline-none";

function ScheduleForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial?: ScheduleDetailRow;
  onSave: (body: ScheduleCreateBody) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? "");
  const [jobName, setJobName] = useState(initial?.job_name ?? "");
  const [triggerKind, setTriggerKind] = useState<TriggerKind>(
    initial?.cron_expr ? "cron" : "interval",
  );
  const [cronExpr, setCronExpr] = useState(initial?.cron_expr ?? "0 */6 * * *");
  const [intervalSeconds, setIntervalSeconds] = useState(
    initial?.interval_seconds != null ? String(initial.interval_seconds) : "3600",
  );
  const [chassisId, setChassisId] = useState(
    initial?.chassis_id != null ? String(initial.chassis_id) : "",
  );
  const [policy, setPolicy] = useState(initial?.policy ?? "skip_if_running");
  const [jitterSeconds, setJitterSeconds] = useState(
    String(initial?.jitter_seconds ?? 0),
  );
  const [paramsText, setParamsText] = useState(
    initial?.params_json ? JSON.stringify(initial.params_json, null, 2) : "{}",
  );
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [paramsError, setParamsError] = useState<string | null>(null);

  // Re-seed when the upstream row changes (e.g. a save just landed).
  useEffect(() => {
    if (!initial) return;
    setName(initial.name);
    setJobName(initial.job_name);
    setTriggerKind(initial.cron_expr ? "cron" : "interval");
    setCronExpr(initial.cron_expr ?? "0 */6 * * *");
    setIntervalSeconds(
      initial.interval_seconds != null ? String(initial.interval_seconds) : "3600",
    );
    setChassisId(
      initial.chassis_id != null ? String(initial.chassis_id) : "",
    );
    setPolicy(initial.policy ?? "skip_if_running");
    setJitterSeconds(String(initial.jitter_seconds ?? 0));
    setParamsText(
      initial.params_json ? JSON.stringify(initial.params_json, null, 2) : "{}",
    );
    setEnabled(initial.enabled);
  }, [initial]);

  const submit = () => {
    let parsedParams: Record<string, unknown> | null = null;
    if (paramsText.trim() && paramsText.trim() !== "null") {
      try {
        parsedParams = JSON.parse(paramsText);
        if (typeof parsedParams !== "object" || Array.isArray(parsedParams)) {
          setParamsError("params must be a JSON object");
          return;
        }
      } catch {
        setParamsError("invalid JSON");
        return;
      }
    }
    setParamsError(null);

    const body: ScheduleCreateBody = {
      name: name.trim(),
      job_name: jobName.trim(),
      cron_expr: triggerKind === "cron" ? cronExpr.trim() : null,
      interval_seconds:
        triggerKind === "interval" ? parseInt(intervalSeconds, 10) || null : null,
      chassis_id: chassisId.trim() ? parseInt(chassisId, 10) : null,
      policy: policy.trim() || null,
      jitter_seconds: parseInt(jitterSeconds, 10) || 0,
      params_json: parsedParams,
      enabled,
    };
    onSave(body);
  };

  return (
    <section className="rounded-lg border border-emerald-800 bg-slate-900 p-4">
      <h2 className="mb-3 text-sm font-medium text-emerald-200">
        {isEdit ? "Edit schedule" : "New schedule"}
      </h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-400">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. nightly-backup-fleet"
            className={inputCls}
            autoFocus={!isEdit}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-400">Job name</span>
          <input
            type="text"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            placeholder="e.g. backup_running_config"
            className={inputCls + " font-mono"}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="text-slate-400">Trigger</span>
          <div className="flex items-center gap-2">
            <select
              value={triggerKind}
              onChange={(e) => setTriggerKind(e.target.value as TriggerKind)}
              className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100"
            >
              <option value="cron">cron expression</option>
              <option value="interval">interval seconds</option>
            </select>
            {triggerKind === "cron" ? (
              <input
                type="text"
                value={cronExpr}
                onChange={(e) => setCronExpr(e.target.value)}
                placeholder="m h dom mon dow — e.g. 0 */6 * * *"
                className={inputCls + " font-mono"}
              />
            ) : (
              <input
                type="number"
                min={1}
                value={intervalSeconds}
                onChange={(e) => setIntervalSeconds(e.target.value)}
                placeholder="seconds"
                className={inputCls + " w-40"}
              />
            )}
          </div>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-400">Chassis ID</span>
          <input
            type="number"
            value={chassisId}
            onChange={(e) => setChassisId(e.target.value)}
            placeholder="(blank for fleet-wide)"
            className={inputCls}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-400">Policy</span>
          <select
            value={policy}
            onChange={(e) => setPolicy(e.target.value)}
            className={inputCls}
          >
            <option value="skip_if_running">skip_if_running</option>
            <option value="queue_after">queue_after</option>
            <option value="cancel_running">cancel_running</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-400">Jitter (seconds)</span>
          <input
            type="number"
            min={0}
            value={jitterSeconds}
            onChange={(e) => setJitterSeconds(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-400">Enabled</span>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-200">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            <span>{enabled ? "enabled" : "disabled"}</span>
          </label>
        </label>
        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="text-slate-400">
            params_json (JSON object; ``{}`` means no params)
          </span>
          <textarea
            value={paramsText}
            onChange={(e) => setParamsText(e.target.value)}
            spellCheck={false}
            rows={4}
            className={inputCls + " font-mono"}
          />
          {paramsError && (
            <span className="text-[10px] text-red-300">{paramsError}</span>
          )}
        </label>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={saving || !name.trim() || !jobName.trim()}
          className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-3 py-1.5 text-xs text-emerald-200 hover:bg-emerald-900/60 disabled:opacity-50"
        >
          <Save className="h-3 w-3" />
          {saving ? "Saving…" : isEdit ? "Save changes" : "Create schedule"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1 rounded border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
        >
          <X className="h-3 w-3" />
          Cancel
        </button>
      </div>
    </section>
  );
}
